import { PayoutHistoryParams, PayoutHistoryXModel } from '@/BoostXpressAdmin/Model/payoutXModel'
import { payoutBXService } from 'BoostXpressAdmin/Services/payoutBXService'
import { handleError } from 'Helpers'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import filterIcon from 'BoostXpressAdmin/Assets/Icon/Users/filter.svg' 
import exportIcon from 'BoostXpressAdmin/Assets/Icon/Stations/export.svg'
import Papa from 'papaparse'
import { HorizontalLoader } from 'Components'

const PayoutHistoryTable = () => {

  const navigate = useNavigate() 
  const [historyRows, setHistoryRows] = useState<PayoutHistoryXModel[]>([])
  const [isLoadingHistory, setIsLoadingHistory] = useState(false)
  const [historyPage, setHistoryPage] = useState(1)
  const [historyTotal, setHistoryTotal] = useState(0)
  const [historyLimit] = useState(10)
  const [historySearch, setHistorySearch] = useState('')
  const [historyStatus, setHistoryStatus] = useState('')
  const [historyUserId, setHistoryUserId] = useState('')
  const [historyDateFrom, setHistoryDateFrom] = useState('')
  const [historyDateTo, setHistoryDateTo] = useState('')
  const [historyMinAmountMajor, setHistoryMinAmountMajor] = useState('')
  const [historyMaxAmountMajor, setHistoryMaxAmountMajor] = useState('')
  const [historySortOrder, setHistorySortOrder] = useState<'ASC' | 'DESC'>('DESC')
  const [showHistoryFilters, setShowHistoryFilters] = useState(false)
    
  const STATUS_OPTIONS = ['PENDING', 'APPROVED', 'COMPLETED', 'REJECTED', 'FAILED']


  const extractPagination = (
    meta: any,
    count: number,
    fallbackPage: number,
    fallbackLimit: number,
    ) => {
    const pagination = meta?.pagination ?? meta?.data?.pagination ?? null
    return {
        total: Number(pagination?.total ?? count),
        perPage: Number(pagination?.per_page ?? pagination?.limit ?? fallbackLimit),
        currentPage: Number(pagination?.current_page ?? pagination?.page ?? fallbackPage),
    }
    }

  const loadHistory = async () => {
    try {
      setIsLoadingHistory(true)
      const params: PayoutHistoryParams = {
        page: historyPage,
        limit: historyLimit,
        status: historyStatus || undefined,
        userId: historyUserId.trim() || undefined,
        search: historySearch.trim() || undefined,
        dateFrom: historyDateFrom || undefined,
        dateTo: historyDateTo || undefined,
        minAmountMajor: historyMinAmountMajor ? Number(historyMinAmountMajor) : undefined,
        maxAmountMajor: historyMaxAmountMajor ? Number(historyMaxAmountMajor) : undefined,
        sortOrder: historySortOrder,
      }

      const res = await payoutBXService.payouts(params)
      const data = Array.isArray(res.data?.data) ? res.data.data : []
      const pagination = extractPagination(res.data?.meta, data.length, historyPage, historyLimit)

      setHistoryRows(data)
      setHistoryTotal(pagination.total)
    } catch (e) {
      setHistoryRows([])
      setHistoryTotal(0)
      handleError({ response: { data: { error: 'Failed to fetch payout history.' } } })
    } finally {
      setIsLoadingHistory(false)
    }
  }

  useEffect(() => {
    loadHistory() 
  }, [])


  const fullName = (firstName?: string | null, lastName?: string | null) =>
  `${firstName ?? ''} ${lastName ?? ''}`.trim() || '—'


  const formatMajorNaira = (value: number) => {
    const formatted = new Intl.NumberFormat('en-NG', { maximumFractionDigits: 0 }).format(value ?? 0)
    return `₦${formatted}`
  }

 
const formatDateTime = (value?: string | null) => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const resetHistoryFilters = () => {
    setHistoryStatus('')
    setHistoryUserId('')
    setHistoryDateFrom('')
    setHistoryDateTo('')
    setHistoryMinAmountMajor('')
    setHistoryMaxAmountMajor('')
    setHistorySortOrder('DESC')
    setHistoryPage(1)
  }


   const exportActiveTableCsv = () => {
   

    const historyCsv = historyRows.map((row) => ({
      attendant: fullName(row.user?.firstName, row.user?.lastName),
      userId: row.user?.resourceId || '—',
      amount: row.amountMajor ?? 0,
      status: row.status,
      bankName: row.bankInfoSnapshot?.bankName || '—',
      bankAccount: row.bankInfoSnapshot?.bankAccountNumber || '—',
      createdAt: row.createdAt,
      processedAt: row.processedAt,
      adminNote: row.adminNote || '',
    }))

    const csvRows = ( historyCsv) as any[]
    const csv = Papa.unparse(csvRows)

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `boostxpress_payouts_history_${new Date().toISOString().slice(0, 10)}.csv`
    document.body.appendChild(a)
    a.click()
    a.remove()
    window.URL.revokeObjectURL(url)
  }

  return ( 
    <div className='bx-payout-board'> 
    <div className='bx-payout-board__toolbar row g-2 align-items-center'>
       <div className='col-12 col-lg'>
            <div className='bx-payout-board__search'>
              <span className='bx-payout-board__search-icon'>
                <svg
                  width='17'
                  height='17'
                  viewBox='0 0 24 24'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M21 21L16.5 16.5M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z'
                    stroke='#868484'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </span>
              <input
                type='text'
                placeholder={
                
                   'Search payout history by user name'
                }
                className='bx-payout-board__search-input'
                value={  historySearch}
                onChange={(e) => {
                
                    setHistorySearch(e.target.value)
                    setHistoryPage(1)
              
                }}
              />
            </div>
          </div>

          <div className='col-6 col-lg-auto position-relative'>
            <button
              type='button'
              className='bx-payout-board__action-btn w-100'
              onClick={() => { 
                  setShowHistoryFilters((value) => !value) 
              
              }}
            >
              <img src={filterIcon} alt='' className='bx-payout-board__action-icon' />
              <span>Filters</span>
            </button>
 
            {showHistoryFilters && (
            <div className='bx-payout-board__filters-dropdown'>
                <div className='bx-payout-board__filters-header'>
                <h5>History Filters</h5>
                <button type='button' onClick={() => setShowHistoryFilters(false)}>
                    x
                </button>
                </div>

                <div className='bx-payout-board__filters-body'>
                <label>
                    Status
                    <select
                    className='form-control'
                    value={historyStatus}
                    onChange={(e) => {
                        setHistoryStatus(e.target.value)
                        setHistoryPage(1)
                    }}
                    >
                    <option value=''>All Statuses</option>
                    {STATUS_OPTIONS.map((status) => (
                        <option key={status} value={status}>
                        {status}
                        </option>
                    ))}
                    </select>
                </label>

                <label>
                    User Resource ID
                    <input
                    type='text'
                    className='form-control'
                    value={historyUserId}
                    onChange={(e) => {
                        setHistoryUserId(e.target.value)
                        setHistoryPage(1)
                    }}
                    />
                </label>

                <label>
                    Date From
                    <input
                    type='date'
                    className='form-control'
                    value={historyDateFrom}
                    onChange={(e) => {
                        setHistoryDateFrom(e.target.value)
                        setHistoryPage(1)
                    }}
                    />
                </label>

                <label>
                    Date To
                    <input
                    type='date'
                    className='form-control'
                    value={historyDateTo}
                    onChange={(e) => {
                        setHistoryDateTo(e.target.value)
                        setHistoryPage(1)
                    }}
                    />
                </label>

                <label>
                    Min Amount
                    <input
                    type='number'
                    className='form-control'
                    value={historyMinAmountMajor}
                    onChange={(e) => {
                        setHistoryMinAmountMajor(e.target.value)
                        setHistoryPage(1)
                    }}
                    />
                </label>

                <label>
                    Max Amount
                    <input
                    type='number'
                    className='form-control'
                    value={historyMaxAmountMajor}
                    onChange={(e) => {
                        setHistoryMaxAmountMajor(e.target.value)
                        setHistoryPage(1)
                    }}
                    />
                </label>

                <label>
                    Sort Order
                    <select
                    className='form-control'
                    value={historySortOrder}
                    onChange={(e) => {
                        setHistorySortOrder(e.target.value as 'ASC' | 'DESC')
                        setHistoryPage(1)
                    }}
                    >
                    <option value='DESC'>DESC</option>
                    <option value='ASC'>ASC</option>
                    </select>
                </label>
                </div>

                <div className='bx-payout-board__filters-footer'>
                <button
                    type='button'
                    className='bx-filter-btn bx-filter-btn--ghost'
                    onClick={resetHistoryFilters}
                >
                    Reset
                </button>
                <button
                    type='button'
                    className='bx-filter-btn'
                    onClick={() => setShowHistoryFilters(false)}
                >
                    Apply
                </button>
                </div>
            </div>
            )}
          </div>

          <div className='col-6 col-lg-auto'>
            <button
              type='button'
              className='bx-payout-board__action-btn w-100'
              onClick={exportActiveTableCsv}
              disabled={
                  isLoadingHistory || !historyRows.length
              }
            >
              <img src={exportIcon} alt='' className='bx-payout-board__action-icon' />
              <span>Export</span>
            </button>
          </div>
       
        <div className='bx-payout-board__table-shell border rounded overflow-hidden'>
            {isLoadingHistory ? (
                <div className='bx-payout-board__loader'>
                <HorizontalLoader />
                </div>
            ) : (
                <div className='table-responsive'>
                    <table className='bx-payout-board__table'>
                        <thead>
                        <tr>
                            <th>Attendant</th>
                            <th>User ID</th>
                            <th>Amount</th>
                            <th>Bank Name</th>
                            <th>Bank Account</th>
                            <th>Status</th>
                            <th>Processed At</th>
                            <th>Created At</th>
                            <th>Action</th>
                        </tr>
                        </thead>
                        <tbody>
                        {historyRows.length === 0 ? (
                            <tr>
                            <td colSpan={9} className='text-center'>
                                No payout history found
                            </td>
                            </tr>
                        ) : (
                            historyRows.map((row) => (
                            <tr key={row.resourceId}>
                                <td>
                                <div className='bx-payout-board__primary-text'>
                                    {fullName(row.user?.firstName, row.user?.lastName)}
                                </div>
                                <div className='bx-payout-board__secondary-text'>
                                    {row.user?.email || '—'}
                                </div>
                                </td>
                                <td>{row.user?.resourceId || '—'}</td>
                                <td>{formatMajorNaira(row.amountMajor ?? 0)}</td>
                                <td>{row.bankInfoSnapshot?.bankName || '—'}</td>
                                <td>{row.bankInfoSnapshot?.bankAccountNumber || '—'}</td>
                                <td>
                                <span className='bx-payout-board__status-pill'>{row.status}</span>
                                </td>
                                <td>{formatDateTime(row.processedAt)}</td>
                                <td>{formatDateTime(row.createdAt)}</td>
                                <td>
                                <button
                                    type='button'
                                    className='bx-payout-board__table-action'
                                    onClick={() => navigate(`/boostxpress/payouts/${row.resourceId}`)}
                                >
                                    View
                                </button>
                                </td>
                            </tr>
                            ))
                        )}
                        </tbody>
                </table>
            </div>  
         )}   
             </div>  
             </div>  
             </div>  
             
             
  )
}

export default PayoutHistoryTable